import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@material-ui/core';
import BigNumber from 'bignumber.js';
import { useContext, useState } from 'react';

import arrow from '../../common/svg/starsStaking/arrow.svg';
import bnbStakingLogo from '../../common/svg/starsStaking/bnbStaking.svg';
import { ReactComponent as ExpandIcon } from '../../common/svg/starsStaking/expand-icon.svg';
import questionMark from '../../common/svg/starsStaking/question-mark.svg';
import starsStakingLogo from '../../common/svg/starsStaking/starsStaking.svg';
import { ModalStateContext } from '../../contexts/app-state-context/modal-state.context';
import { createHarvestModal } from '../common/modal/staking/HarvestModal.component';
import { createHarvestWithdrawModal } from '../common/modal/staking/HarvestWithdrawModal.component';
import { createWithdrawModal } from '../common/modal/staking/WithdrawModal.component';
import { Button } from '../core/button/Button';
import { Tooltip } from '../core/tooltip';
import { CountDown } from './CountDown.component';
import styles from './DesktopRewardsAccordion.module.css';

export interface Props {
  poolName: string;
  apr: string;
  totalDepositedLP: number | undefined;
  timeLock: number | undefined;
  poolId: number;
  depositId: number;
  rewardAmount: number | undefined;
  isActive: boolean;
  getRewardInfo: () => void;
  endBlock: number;
  starsPerBlock: number;
  totalPoolDepositedLP: number;
  blockNumber: number;
}

export const DesktopRewardsAccordion: React.FC<Props> = props => {
  const [clicked, setClicked] = useState(false);
  const { setModal } = useContext(ModalStateContext);

  const handleClickHarvestModal = () => {
    setModal(
      createHarvestModal(
        props.rewardAmount,
        props.poolId,
        props.depositId,
        props.getRewardInfo,
      ),
    );
  };

  const handleClickWithdrawModal = () => {
    setModal(
      createWithdrawModal(
        props.totalDepositedLP,
        props.poolId,
        props.depositId,
        props.getRewardInfo,
        props.timeLock ?? 0,
        props.rewardAmount ?? 0,
        props.poolName,
        parseFloat(props.apr),
        props.endBlock,
        props.starsPerBlock,
        props.totalPoolDepositedLP,
      ),
    );
  };

  const handleClickHarvestWithdrawModal = () => {
    setModal(
      createHarvestWithdrawModal(
        props.rewardAmount,
        props.totalDepositedLP,
        props.poolId,
        props.depositId,
        props.getRewardInfo,
      ),
    );
  };

  const toggle = () => {
    setClicked(!clicked);
  };

  return (
    <Accordion
      className={`w-full`}
      style={
        clicked
          ? {
              backgroundColor: '#000000',
              border: '1px solid rgba(255, 255, 255, .1)',
            }
          : { backgroundColor: '#000000' }
      }
      onChange={() => toggle()}
    >
      <AccordionSummary
        style={{ backgroundColor: 'unset' }}
        expandIcon={<ExpandIcon />}
      >
        <div className="flex flex-wrap w-full mr-10 items-center gap-4 justify-between">
          {!props.isActive ? (
            <div
              className={
                styles.inactive + ' flex justify-center items-center px-1'
              }
            >
              <div
                style={{ backgroundColor: '#EB324D' }}
                className={styles.circle}
              />
              Closed Pool
            </div>
          ) : (
            <div
              className={
                styles.active + ' flex justify-center items-center px-1'
              }
            >
              <div
                style={{ backgroundColor: '#03C04F' }}
                className={styles.circle}
              />
              Active Pool
            </div>
          )}
          <div className="flex flex-row gap-2">
            <div className="flex flex-col gap-3 items-center">
              {props.poolName === 'The Oscar’s Pool' ? (
                <div className="flex">
                  <img
                    height="30"
                    className={'self-center'}
                    src={bnbStakingLogo}
                    alt="bnbStakingLogo z-0"
                  />
                  <img
                    height="30"
                    className={'-ml-4 z-10 self-center'}
                    src={starsStakingLogo}
                    alt="starsStakingLogo"
                  />
                </div>
              ) : (
                <img height="30" src={starsStakingLogo} alt="" />
              )}
              <div className={styles.currencyText}>MOGUL</div>
            </div>

            <img className="mb-8" src={arrow} alt="" />

            <div className="flex flex-col gap-3">
              <img height="30" src={starsStakingLogo} alt="" />
              <div className={styles.currencyText}>MOGUL</div>
            </div>
          </div>
          <div className={styles.VIPHeader}>{props.poolName}</div>
          <div className="flex flex-col text-center gap-1">
            <div className={styles.infoHeader}>
              APR{' '}
              <Tooltip
                title={
                  'Annual percentage rate, the interest rate earned over the period of a year'
                }
                placement="top"
              >
                <img src={questionMark} alt="" />
              </Tooltip>
            </div>
            <div className={styles.content}>{props.apr}%</div>
          </div>
          <div className="flex flex-col text-center gap-1">
            <div className={styles.infoHeader}>
              Total Amount Staked{' '}
              <Tooltip
                title={
                  'Total number of MOGUL staked in this pool for your current wallet'
                }
                placement="top"
              >
                <img src={questionMark} alt="" />
              </Tooltip>
            </div>
            <div className={styles.content}>
              {new BigNumber(props.totalDepositedLP ?? 0).toString(10)}
            </div>
          </div>
          <div className="flex flex-col text-center gap-1">
            <div className={styles.infoHeader}>
              Reward Amount{' '}
              <Tooltip
                title={'How many tokens earned as a reward from staking'}
                placement="top"
              >
                <img src={questionMark} alt="" />
              </Tooltip>
            </div>
            <div className={styles.content}>{props.rewardAmount}</div>
          </div>
          <div className="flex flex-col text-center gap-1">
            <div className={styles.infoHeader}>
              Remaining Lockup Period{' '}
              <Tooltip
                title={
                  'The length of time left that your tokens need to be staked in order to qualify for reward harvesting at the end of the pool period'
                }
                placement="top"
              >
                <img src={questionMark} alt="" />
              </Tooltip>
            </div>
            <div className={'text-white'}>
              {props.timeLock ? (
                <CountDown
                  startDate={
                    new Date(props.timeLock * 3000 + new Date().getTime())
                  }
                />
              ) : (
                <CountDown startDate={new Date(0)} />
              )}
            </div>
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <div className="w-full flex flex-row justify-evenly items-center gap-20 py-5 px-20">
          <div className={'w-1/4 text-center ' + styles.text}>
            WHAT WOULD YOU LIKE TO DO?
          </div>
          <div className="w-full flex justify-evenly flex-wrap gap-2">
            {props.blockNumber > props.endBlock ? (
              <Button
                onClick={handleClickHarvestModal}
                className={
                  styles.poolButton +
                  ' w-1/4 flex justify-center items-center pointer'
                }
                disabled={
                  !(props.timeLock === undefined || props.timeLock === 0) ||
                  props.rewardAmount === 0
                }
              >
                HARVEST
              </Button>
            ) : (
              <Tooltip
                title={
                  'Rewards can be harvested after the staking for this pool has ended'
                }
                placement="top"
              >
                <Button
                  className={
                    styles.poolButtonDisabled +
                    ' w-1/4 flex justify-center items-center cursor-default'
                  }
                >
                  HARVEST
                </Button>
              </Tooltip>
            )}
            <Button
              onClick={handleClickWithdrawModal}
              className={
                styles.poolButton +
                ' w-1/4 flex justify-center items-center pointer'
              }
              disabled={props.totalDepositedLP === 0}
            >
              WITHDRAW
            </Button>
            {props.blockNumber > props.endBlock ? (
              <Button
                onClick={handleClickHarvestWithdrawModal}
                className={
                  styles.poolButton +
                  ' w-1/4 flex justify-center items-center pointer'
                }
                disabled={
                  !(props.timeLock === undefined || props.timeLock === 0) ||
                  props.rewardAmount === 0 ||
                  props.totalDepositedLP === 0
                }
              >
                HARVEST AND WITHDRAW
              </Button>
            ) : (
              <Tooltip
                title={
                  'Rewards can be harvested after the staking for this pool has ended'
                }
                placement="top"
              >
                <Button
                  className={
                    styles.poolButtonDisabled +
                    ' w-1/4 flex justify-center items-center cursor-default'
                  }
                >
                  HARVEST AND WITHDRAW
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};
